import { useState } from "react";
import { Plus, Minus } from "@phosphor-icons/react";

const FAQS = [
  {
    q: "What is price protection?",
    a: "Many NZ retailers - both online and in-store - have a policy that lets you claim back the difference if the price of something you bought drops within a short window (PB Tech 30 days, JB Hi-Fi 14, Harvey Norman 7). It's a standard part of how retailers compete on price. The catch is that you have to notice the drop yourself and request it - that's where PriceGuard comes in. We watch the price for a full six weeks after every tracked purchase so we catch second-wave drops too, and we draft the polite refund email for you the moment the retailer's claim window is open.",
  },
  {
    q: "How do I use PriceGuard?",
    a: "Sign up, add a recent purchase by pasting the product link, and we'll watch the price daily for six weeks. If it drops inside the retailer's published price-adjustment window (PB Tech 30 days, JB Hi-Fi 14, Harvey Norman 7), we'll email you and draft a polite, retailer-ready refund request. We keep watching for the full six weeks even after the claim window closes, so we catch second-wave drops the retailer may make weeks later.",
  },
  {
    q: "Is PriceGuard affiliated with any retailer?",
    a: "No - we're an independent NZ-built tool. We work on top of each retailer's existing, publicly published price-protection policy. We see ourselves as partners that help shoppers actually use the protection retailers already offer, and we're open to formal partnerships with any retailer that wants to work with us.",
  },
  {
    q: "How does PriceGuard make money?",
    a: "Two ways, both designed to keep your interests aligned with ours. First, we'll soon offer a Pro plan with extra features (unlimited storage, AI-drafted warranty claims, insurance exports). Second, we may earn a small affiliate referral if you choose to buy through a link we surface - it never changes the price you pay, and we never recommend a retailer based on commission. We don't sell your data. Ever.",
  },
  {
    q: "Is my data safe? Can other people see what I've bought?",
    a: "Yes, your data is private. Everything is encrypted in transit (HTTPS), passwords are hashed with industry-standard bcrypt, and your purchase history is only ever visible to you. We never sell or share customer data with retailers or third parties. Two-factor authentication is coming with the Pro tier.",
  },
  {
    q: "What does it cost?",
    a: "Free forever for up to 5 items. That includes daily price-protection monitoring, AI-drafted request emails, and the receipt vault with warranty alerts. A Pro plan ($4.99/month NZD) is coming for power users who want unlimited storage, auto-claim filing, insurance schedule exports, and more. Cancel anytime - no lock-ins.",
  },
  {
    q: "Will retailers actually honor my request?",
    a: "Most leading NZ retailers honor their published price-protection policies - that's why we mirror the polite, retailer-friendly tone they expect in our drafted emails. We've designed the entire flow to fit naturally into normal customer service, not to be adversarial. Outcomes ultimately depend on each retailer's policy and discretion, but the process is straightforward when the terms apply.",
  },
  {
    q: "Which retailers do you support, and which are coming?",
    a: "We support the leading NZ retailers that publish price-protection policies, with more being added every month. If your retailer isn't yet supported, you can still wishlist any product and we'll watch it. We're expanding coverage based on what our members are tracking - let us know who you'd like to see next.",
  },
  {
    q: "What's the actual price-protection window for each retailer?",
    a: "PB Tech's PBCare programme is the most clearly published one in NZ - they will refund the difference if the same PB Tech price drops within 30 days of your purchase. Harvey Norman NZ and JB Hi-Fi NZ don't publish a specific post-purchase window online, so we use conservative defaults (7 and 14 days respectively) and our draft email always asks politely under whatever policy currently applies. We show you the live policy on the detail page for each purchase. When in doubt, the retailer's customer service team is the source of truth.",
  },
  {
    q: "Will a NZ retailer match a competitor's lower price after I've bought?",
    a: "Some will, some won't, and most don't publish a clear post-purchase competitor price-match policy in New Zealand. JB Hi-Fi NZ has a published pre-purchase competitor match. PB Tech and Harvey Norman NZ don't publicly commit to matching a competitor after purchase, but plenty of shoppers report success when they ask politely with the link to the cheaper listing. Our cross-retailer scan surfaces the opportunity; the request is always yours to send, and the call is always the retailer's.",
  },
];

export default function FAQ() {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section id="faq" className="border-b border-[#e4e4e7] bg-white" data-testid="faq-section">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="pg-overline mb-4">Questions, answered</div>
            <h2 className="font-heading text-4xl lg:text-5xl font-extrabold tracking-tight">
              Honest answers<br/>before you sign up<span className="text-[#002FA7]">.</span>
            </h2>
            <p className="text-[#52525b] mt-6 max-w-md leading-relaxed text-sm">
              Anything we missed? Email <a href="mailto:hello@priceguard.nz" className="text-[#002FA7] font-bold underline" data-testid="faq-contact-link">hello@priceguard.nz</a> - a real Kiwi reads every message.
            </p>
          </div>
          <div className="lg:col-span-8">
            <div className="border border-[#e4e4e7] bg-white divide-y divide-[#e4e4e7]">
              {FAQS.map((item, i) => {
                const open = openIdx === i;
                return (
                  <div key={item.q} data-testid={`faq-item-${i}`}>
                    <button
                      type="button"
                      onClick={() => setOpenIdx(open ? -1 : i)}
                      className="w-full flex items-start justify-between gap-4 text-left px-5 py-5 hover:bg-[#f7f7f8] transition-colors"
                      data-testid={`faq-toggle-${i}`}
                      aria-expanded={open}
                    >
                      <span className="font-heading font-bold text-lg tracking-tight leading-snug">{item.q}</span>
                      <span className="mt-1 flex-shrink-0 text-[#002FA7]">
                        {open ? <Minus size={18} weight="bold" /> : <Plus size={18} weight="bold" />}
                      </span>
                    </button>
                    {open && (
                      <div className="px-5 pb-6 pt-1 text-[#0a0a0a] text-base leading-relaxed" data-testid={`faq-answer-${i}`}>
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
